// One patient's SBAR row inside a Shift Handover (nursing-ops sprint, Epic 9
// / D11). Editable only while the parent handover is still "draft" — the
// backend enforces the same guard (ManageShiftHandoverPatientView.put), so a
// save attempt after submit/acknowledge would come back in `skipped` even
// though the envelope says Success; we gate the inputs client-side too so
// that never happens in the first place.
import React, { useEffect, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Button, Chip, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { updateShiftHandoverPatients } from "../api/nurseApi";

const PRIORITY_OPTIONS = [
  { value: "routine", label: "Routine" },
  { value: "watch", label: "Watch" },
  { value: "critical", label: "Critical" },
];
const PRIORITY_COLOR = { routine: "default", watch: "warning", critical: "error" };
const SBAR_KEYS = ["situation", "background", "assessment", "recommendation", "special_instructions", "priority"];

function fieldsFrom(patient) {
  return {
    situation: patient.situation || "",
    background: patient.background || "",
    assessment: patient.assessment || "",
    recommendation: patient.recommendation || "",
    special_instructions: patient.special_instructions || "",
    priority: patient.priority || "routine",
  };
}

export default function ShiftHandoverPatientCard({ patient, editable, onSaved }) {
  const [form, setForm] = useState(() => fieldsFrom(patient));
  const [saving, setSaving] = useState(false);

  // Re-sync when the parent list refetches with saved values (or a different
  // patient row is passed in).
  useEffect(() => {
    setForm(fieldsFrom(patient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id, patient.situation, patient.background, patient.assessment, patient.recommendation, patient.special_instructions, patient.priority]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const baseline = fieldsFrom(patient);
  const dirty = SBAR_KEYS.some((k) => form[k] !== baseline[k]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateShiftHandoverPatients({ patients: [{ id: patient.id, ...form }] });
      if (res?.status !== "Success") {
        toast.error(res?.message || "Failed to save SBAR");
        return;
      }
      const skipped = res?.skipped || [];
      const wasSkipped = skipped.find((s) => String(s.id) === String(patient.id));
      if (wasSkipped) {
        toast.error(wasSkipped.reason || "This patient's SBAR could not be saved");
        return;
      }
      toast.success("SBAR saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save SBAR");
    } finally {
      setSaving(false);
    }
  };

  const name = patient.patient?.patient_name || "Unknown patient";
  const bed = patient.location?.bed_code || "—";
  const wardName = patient.location?.ward_name || patient.location?.ward_code || "";
  const priorityLabel = (PRIORITY_OPTIONS.find((p) => p.value === form.priority) || {}).label || form.priority;
  const pendingCount = (patient.pending_tasks || []).length;

  return (
    <Accordion sx={{ border: `1px solid ${NURSE_COLORS.border}`, "&:before": { display: "none" } }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", width: "100%", pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {name}{" "}
            <Typography component="span" variant="caption" color="text.secondary">
              ({bed}{wardName ? `, ${wardName}` : ""})
            </Typography>
          </Typography>
          {pendingCount > 0 && <Chip size="small" variant="outlined" label={`${pendingCount} pending task(s)`} />}
          <Chip size="small" label={priorityLabel} color={PRIORITY_COLOR[form.priority] || "default"} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small" label="Priority" value={form.priority} disabled={!editable}
              onChange={(e) => set({ priority: e.target.value })}
            >
              {PRIORITY_OPTIONS.map((p) => (<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Situation" value={form.situation} disabled={!editable}
              onChange={(e) => set({ situation: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Background" value={form.background} disabled={!editable}
              onChange={(e) => set({ background: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Assessment" value={form.assessment} disabled={!editable}
              onChange={(e) => set({ assessment: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Recommendation" value={form.recommendation} disabled={!editable}
              onChange={(e) => set({ recommendation: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Special instructions" value={form.special_instructions} disabled={!editable}
              onChange={(e) => set({ special_instructions: e.target.value })}
            />
          </Grid>
        </Grid>
        {editable && (
          <Stack direction="row" sx={{ mt: 1.5 }}>
            <Button size="small" variant="contained" disabled={!dirty || saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save SBAR"}
            </Button>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
